import React, { useContext } from 'react';
import ApolloError from '@shared/components/ApolloError';
import EventTeamTables from '../../inputs/EventTeamTables';
import TravelInformation from '../../inputs/TravelInformation';
import AtlSelection from '../../inputs/AtlSelection';
import EventDates from '../../inputs/EventDates';
import OrgOrgUnitSelection from '../../inputs/OrgOrgUnitSelection';
import useAvailableAtlList from '../../../../hooks/useAvailableAtlList';
import ReviewChangeDateRequest from './ReviewChangeDateRequest';
import { CHANGE_DATE_REQUEST_STATUS } from '../../../../constants/ChangeDateRequestConstants';
import SowStatusSectionWrapper from '../SowStatus/SowStatusSectionWrapper';
import AtlRankingTable from '../../inputs/AtlRankingTable';
import EventStatusWarnings from '../EventStatusWarnings';
import { useExcludedDateIntervals } from '../../../shared/hooks/useExcludedDateIntervals';
import { EventSettingsContext } from '../../../shared/contexts/EventSettingsContext';
import { useAtlSelectionType } from '../../../shared/hooks/useAtlSelectionType';
import ObservationAndCoaching from './ObservationAndCoaching/ObservationAndCoaching';
import { showWarningToast } from '@shared/services/toastService';
import { useShowUnavailableAtmsStore } from './AdminEditEventStore';
import { useShallow } from 'zustand/shallow';
import { userAssignmentService } from '../shared/userAssignmentService';
import { EVENT_ASSIGNMENTS_TABLE_MODE } from '../../inputs/constants/EventAssignmentsTableMode';

/**
 * @typedef {object} AdminEditEventStoredEventData
 * @property {Object} [Atl]
 * @property {Object} [AtlObserver]
 * @property {number} OrgId
 * @property {Array<*>} WarningTypes
 * @property {string} IntendedStartDate
 * @property {number} Stage
 */

/**
 * @typedef {object} AdminEditEventProps
 * @property {number} eventId
 * @property {boolean} [eventLoading]
 * @property {Object} [eventFormData]
 * @property {(eventFormData: Object | ((prev: Object) => Object)) => void} setEventFormData
 * @property {(startDateName: string, endDateName: string) => (startDate: Date, endDate: Date) => void} getOnDateRangeChangedHandler
 * @property {Array<*>} [organizations]
 * @property {Object} [changeDateRequestData]
 * @property {(status: number, declineComment?: string) => void | Promise<void>} handleChangeDateRequestReviewed
 * @property {boolean} [orgUnitsLoading]
 * @property {AdminEditEventStoredEventData} [storedEventData]
 * @property {Array<*>} [filteredAssignmentData]
 * @property {Object} [validationErrors]
 */

/**
 * @param {AdminEditEventProps} props
 */
const AdminEditEvent = props => {
  const {
    eventId,
    eventFormData,
    storedEventData,
    filteredAssignmentData,
    changeDateRequestData,
    organizations,
    handleChangeDateRequestReviewed,
    validationErrors,
    setEventFormData,
    getOnDateRangeChangedHandler,
    orgUnitsLoading = false,
    eventLoading = false
  } = props;

  const settings = useContext(EventSettingsContext);
  const [showUnavailableAtms, setShowUnavailableAtms] = useShowUnavailableAtmsStore(
    useShallow(state => [state.showUnavailableAtms, state.setShowUnavailableAtms])
  );

  const {
    data: excludedDateIntervals,
    error: appraiserUnavailableError,
    loading: appraiserUnavailableLoading
  } = useExcludedDateIntervals(eventId);

  const { availableAtlList, availableAtlsLoading, availableAtlsError } = useAvailableAtlList(
    eventFormData.startDate,
    eventFormData.endDate,
    eventFormData.atl,
    eventFormData.atlObserver,
    setEventFormData,
    eventId
  );

  const atlSelectionType = useAtlSelectionType(eventLoading, storedEventData?.Stage, settings);

  const handleChangeDateReviewWithWarnings = async (status, declineComment) => {
    await handleChangeDateRequestReviewed(status, declineComment);

    const unavailablePrimaryAtms = filteredAssignmentData?.filter(
      x => x.isUnavailableAfterDCR && userAssignmentService.isPrimary(x.role)
    );

    if (status === CHANGE_DATE_REQUEST_STATUS.APPROVED && unavailablePrimaryAtms?.length > 0) {
      showWarningToast(
        'The following team member(s) are not available for this new date: ' +
          unavailablePrimaryAtms
            .map(x => x.name)
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
            .join(', ')
      );
    }

    setShowUnavailableAtms(false);
  };

  if (availableAtlsError || appraiserUnavailableError) {
    return <ApolloError />;
  }

  return (
    <div>
      {settings.sowSettings?.canEdit && (
        <SowStatusSectionWrapper
          loading={eventLoading || orgUnitsLoading || availableAtlsLoading}
          eventFormData={eventFormData}
          availableAtlList={availableAtlList}
          organizations={organizations}
          setEventFormData={setEventFormData}
          validationErrors={validationErrors}
        />
      )}
      <section className='page-section'>
        <OrgOrgUnitSelection
          organizations={organizations}
          selectedOrgId={storedEventData?.OrgId}
          selectedOrgUnitIds={eventFormData.orgUnitIds}
          setSelections={setEventFormData}
          loading={orgUnitsLoading}
          displayOnlyUnitSelector={true}
          hasOrgUnitError={validationErrors.orgUnitIds}
        />
        <EventStatusWarnings
          showAppraisalDetailStatus={settings.showAppraisalDetailStatus}
          warningTypes={storedEventData?.WarningTypes}
        />
      </section>
      <EventDates
        selectedStartDate={eventFormData.intendedStartDate}
        selectedEndDate={eventFormData.intendedEndDate}
        loading={eventLoading}
        onDateRangeChanged={getOnDateRangeChangedHandler('intendedStartDate', 'intendedEndDate')}
        label='Intended Event Dates'
        name='eventIntendedDates'
      />
      <EventDates
        selectedStartDate={eventFormData.startDate}
        selectedEndDate={eventFormData.endDate}
        loading={eventLoading}
        onDateRangeChanged={getOnDateRangeChangedHandler('startDate', 'endDate')}
        excludedDateIntervals={excludedDateIntervals}
        label='Event Dates'
        name='eventDates'
        disabled={appraiserUnavailableLoading}
      />
      {changeDateRequestData &&
        changeDateRequestData.Status === CHANGE_DATE_REQUEST_STATUS.PENDING_ADMIN_APPROVAL && (
          <ReviewChangeDateRequest
            handleChangeDateRequestReviewed={handleChangeDateReviewWithWarnings}
            changeDateRequestData={changeDateRequestData}
          />
        )}
      <AtlSelection
        atlList={availableAtlList}
        eventFormData={eventFormData}
        setSelections={setEventFormData}
        selectionType={atlSelectionType}
        loading={eventLoading}
        availableAtlsLoading={availableAtlsLoading}
        eventATLExternalId={storedEventData?.Atl?.ExternalUserId}
        eventATLObserverExternalId={storedEventData?.AtlObserver?.ExternalUserId}
        validationErrors={validationErrors}
      />

      {settings.showAtlRankingTable && (
        <AtlRankingTable
          atlSponsorRankings={eventFormData.atlSponsorRankings}
          mode={EVENT_ASSIGNMENTS_TABLE_MODE.ATL_RANKING}
          loading={eventLoading}
        />
      )}

      <EventTeamTables
        eventTeams={eventFormData.eventTeams}
        setEventFormData={setEventFormData}
        loading={eventLoading}
        showEventAssignment={settings.showEventAssignment}
        eventAssignments={filteredAssignmentData}
        showUnavailableAtms={showUnavailableAtms}
        showAssignmentRank={settings.showAssignmentRank}
        canAssignATMs={settings.canAssignATMs}
      />

      {settings.showAtmObservationSection && (
        <ObservationAndCoaching
          eventAssignments={eventFormData.eventAssignments}
          atmObservationAssignments={eventFormData.atmObservationAssignments}
          setEventFormData={setEventFormData}
        />
      )}

      <TravelInformation
        logisticalNotes={eventFormData.logisticalNotes}
        deliveryMethod={eventFormData.deliveryMethod}
        regions={eventFormData.regions}
        setEventFormData={setEventFormData}
        loading={eventLoading}
      />
    </div>
  );
};

export default AdminEditEvent;
